import { organizationApi } from '@/api/account-management/organizationApi';
import { brandApi } from '@/api/account-management/brandApi';
import { locationApi } from '@/api/account-management/locationApi';
import { type Brand } from '@/models/account-management/Brand';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fillHeight } from "../../theme";
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';


export interface BrandListProps {
    organizationId?: string;
}

const BrandList = ({ organizationId }: BrandListProps) => {
    const qc = useQueryClient();
    const { data: brandList, isFetching: isGridLoading } = useQuery<Brand[]>({ queryKey: ['brands', organizationId], enabled: !!organizationId, queryFn: () => organizationApi.listBrands(organizationId!), staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: false }); // refetch on every visit; isFetching drives the grid loading overlay so refreshes are visible
    const { mutateAsync: deleteBrand } = useMutation({ mutationFn: brandApi.delete, onSuccess: () => handleDeleteSuccess(), onError: () => handleDeleteError() }); //react-query to delete the brand
    const toast = useToast();
    const strings = useLocalizedStrings();
    const navigate = useNavigate();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.adminConfiguration);
    const canUpdate = perms.canUpdate(Perm.adminConfiguration);
    const canDelete = perms.canDelete(Perm.adminConfiguration);

    // Grid columnn definitions
    const columns: GridColDef<Brand>[] = [
        {
            field: 'name',
            headerName: strings.name,
            flex: 1,
        },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [
                <DataGridEditButton id={params.id.toString()} onClick={handleEditClick} aria-label="edit brand" readOnly={!canUpdate} />,
                <DataGridDeleteButton id={params.id.toString()} getWarningMessage={getDeleteWarningMessage} onClick={handleDeleteClick} aria-label="delete brand" hidden={!canDelete} />
            ],
        },
    ];

    // Add button handler
    const handleAddClick = () => {
        if (!organizationId) return;
        navigate(`/touchtab/admin/configuration/brands/new?orgId=${organizationId}`);
    };

    // Edit (row) button handler
    const handleEditClick = (id: string) => {
        const found = brandList?.find(o => o.id === id);
        if (!found) return;
        navigate(`/touchtab/admin/configuration/brands/${found.id}`);
    };

    // Get the warning message to display when user tries to delete a row
    const getDeleteWarningMessage = (id: string) => {
        const brand = brandList?.find(o => o.id === id);
        return strings.brandConfirmDeleteMessage.replace("{{name}}", brand?.name ?? "");
    }

    // Delete (row) button handler
    const handleDeleteClick = async (id: string) => {
        // A brand can only be deleted once ALL of its locations are permanently deleted (the backend enforces
        // this too). Pre-check both live and soft-deleted locations so we can give a clear reason rather than a
        // generic failure. Soft-deleted locations count — they must be permanently deleted first.
        try {
            const [live, deleted] = await Promise.all([brandApi.listLocations(id), locationApi.listDeleted(id)]);
            const total = (live?.length ?? 0) + (deleted?.length ?? 0);
            if (total > 0) {
                toast({ severity: 'error', message: strings.brandDeleteHasLocations.replace('{{count}}', String(total)) });
                return;
            }
        } catch {
            // If the pre-check itself fails, fall through and let the delete attempt surface any error.
        }
        await deleteBrand(id);
    };

    const handleDeleteSuccess = async () => {
        toast({ severity: "success", message: strings.brandDeleteSuccess });
        await qc.invalidateQueries({ queryKey: ['brands', organizationId] }); // Invalidate the brands query to refresh the list
    }

    const handleDeleteError = () => {
        toast({ severity: "error", message: strings.brandDeleteError });
    }

    return (
        <>
            {/* fillHeight is a shared sx constant that will make the element fill the available height of its parent */}
            <Box sx={{ ...fillHeight, overflow: 'hidden' }}>
                {canCreate &&
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                size="input"
                                startIcon={<AddIcon />}
                                disabled={!organizationId}
                                onClick={handleAddClick}
                            >
                                {strings.brandNewTitle}
                            </Button>
                        </Box>
                        <Spacer y={2} />
                    </>
                }
                <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                    <DataGrid
                        rows={brandList}
                        columns={columns}
                        autoPageSize={true}
                        loading={!organizationId ? false : isGridLoading}
                        localeText={{ noRowsLabel: !organizationId ? strings.selectOrganization : strings.brandsNotFound }}
                    />
                </Box>
            </Box>
        </>
    );
};

export default BrandList;
